// =====================================================================
// Conges.jsx - Pagina principala unificata Conges (Day 4 - 20-05-2026)
// =====================================================================
// Layout Option C: 1 link in sidebar (/conges), 4 vue-uri interne.
//
// Tabs:
//   1. Mes conges          - MesConges embedded (soldes + demande + historique)
//   2. A valider           - TabValider din AdminConges (admin)
//   3. Encodage direct     - TabDirect din AdminConges (admin)
//   4. Cotes annuelles     - TabCotes din AdminConges (admin)
//
// Permission gate: needs leaves.create.own SAU leaves.approve.all
// Default tab: admin -> "valider"; user pur -> "mes-conges"
// =====================================================================

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import MesConges from './MesConges.jsx';
import { TabValider, TabDirect, TabCotes, TabHistorique } from './admin/AdminConges.jsx';
import LeavesTimelineView from './admin/LeavesTimelineView.jsx';
import SoldesParUtilisateur from './admin/SoldesParUtilisateur.jsx';

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

export default function Conges() {
  const { can } = useAuth();

  const canSubmitOwn = can('leaves.create.own');
  const canApprove   = can('leaves.approve.all');

  // Default tab: admin -> "valider" (focus pe ce are de făcut); user pur -> "mes-conges"
  const defaultTab = canApprove ? 'valider' : (canSubmitOwn ? 'mes-conges' : 'mes-conges');

  // Day 9: support URL hash to force tab
  const location = useLocation();
  const VALID_TABS = ['mes-conges', 'valider', 'direct', 'cotes', 'historique', 'timeline', 'soldes-users'];
  const hashTab = location.hash.replace('#', '');
  const initialTab = VALID_TABS.includes(hashTab) ? hashTab : defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (VALID_TABS.includes(hashTab)) setActiveTab(hashTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const TAB_LABELS = {
    'mes-conges': 'Mes congés',
    'valider':    'À valider',
    'direct':     'Encodage direct',
    'cotes':      'Quotas annuels',
    'historique': 'Historique',
    'timeline':   'Vue d’ensemble',
    'soldes-users': 'Soldes par utilisateur',
  };

  // Access gate
  if (!canSubmitOwn && !canApprove) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Congés</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Congés</h1>
        <span className="hide-mobile" style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>gestion congés &amp; soldes</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Demandes de congés, validation et gestion des quotas annuels.
      </p>

      <div className="hide-mobile">
        <Breadcrumbs items={[
          { label: 'Congés', onClick: () => setActiveTab(defaultTab) },
          { label: TAB_LABELS[activeTab] || activeTab },
        ]} />
      </div>

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canSubmitOwn && (
          <button style={tabBtn(activeTab === 'mes-conges')} onClick={() => setActiveTab('mes-conges')}>
            📥 Mes congés
          </button>
        )}
        {canApprove && (
          <button style={tabBtn(activeTab === 'valider')} onClick={() => setActiveTab('valider')}>
            ✓ À valider
          </button>
        )}
        {canApprove && (
          <button style={tabBtn(activeTab === 'direct')} onClick={() => setActiveTab('direct')}>
            ✎ Encodage direct
          </button>
        )}
        {canApprove && (
          <button style={tabBtn(activeTab === 'cotes')} onClick={() => setActiveTab('cotes')}>
            📊 Quotas annuels
          </button>
        )}
        {can('leaves.cancel.approved') && (
          <button style={tabBtn(activeTab === 'historique')} onClick={() => setActiveTab('historique')}>
            ≡ Historique
          </button>
        )}
        {can('leaves.read.all') && (
          <button style={tabBtn(activeTab === 'timeline')} onClick={() => setActiveTab('timeline')}>
            📅 Vue d’ensemble
          </button>
        )}
        {can('users.read.all') && (
          <button style={tabBtn(activeTab === 'soldes-users')} onClick={() => setActiveTab('soldes-users')}>
            👥 Soldes par utilisateur
          </button>
        )}
      </div>

      {activeTab === 'mes-conges' && canSubmitOwn && <MesConges embedded />}
      {activeTab === 'valider'    && canApprove   && <TabValider />}
      {activeTab === 'direct'     && canApprove   && <TabDirect />}
      {activeTab === 'cotes'      && canApprove   && <TabCotes />}
      {activeTab === 'historique' && can('leaves.cancel.approved') && <TabHistorique />}
      {activeTab === 'timeline'   && can('leaves.read.all') && <LeavesTimelineView onNavigateToHistorique={() => setActiveTab('historique')} />}
      {activeTab === 'soldes-users' && can('users.read.all') && <SoldesParUtilisateur />}
    </div>
  );
}
// force-rebuild 2026-05-22-20-47-42
